/**
 * WaveModSource — exposes the live audio at a chosen tap point as a
 * mod source in the Mod Matrix. Same idea as wiring an LFO to a
 * parameter, except the "LFO" is whatever's flowing through your
 * patch (master, the wavefolder's output, etc.). The signal feeds
 * the matrix at audio rate, so modulation tracks the wave's actual
 * shape sample-by-sample — handy for cross-modulation tricks like
 * "filter cutoff follows the master signal" or "OSC width follows
 * the FOLD output".
 *
 * Architecture:
 *
 *   tapSource ─► input passthrough ─► attenuator gain ─► output
 *                                                       │
 *                                                       └─► registered as
 *                                                           the 'wave' mod
 *                                                           source on the
 *                                                           ModRouter
 *
 * The attenuator scales the (potentially loud) audio signal down to
 * something more like the unit-bipolar range mod sources are sized
 * around. The mod-matrix depth knob then attenuates further per
 * connection. Without the attenuator, hooking master → cutoff would
 * slam the filter into max + 4kHz on every kick.
 *
 * The output GainNode is allocated once at construction and registered
 * with the router immediately. Swapping tap sources just rewires the
 * input side; the output node identity is stable so existing mod
 * connections don't need to be torn down.
 */

import type { AudioEngine } from '../AudioEngine';
import type { ModRouter } from './ModRouter';

/** Stable id used by the router and persisted in presets. */
export const WAVE_MOD_SOURCE_ID = 'wave';

/**
 * Default attenuation. The unit-bipolar mod sources (LFO at depth=1)
 * cover roughly ±1 → modScale; an audio signal at master taps tend
 * to peak around ±0.3..0.7. Bringing the attenuator near unity makes
 * the WAVE source feel similar in depth-knob territory to LFO.
 */
const DEFAULT_GAIN = 0.5;

export class WaveModSource {
  private readonly engine: AudioEngine;
  private readonly router: ModRouter;
  private readonly inputPassthrough: GainNode;
  private readonly attenuator: GainNode;
  private currentSource: AudioNode | null = null;
  private registered = false;

  constructor(engine: AudioEngine, router: ModRouter) {
    this.engine = engine;
    this.router = router;
    const ctx = engine.context;
    this.inputPassthrough = ctx.createGain();
    this.inputPassthrough.gain.value = 1;
    this.attenuator = ctx.createGain();
    this.attenuator.gain.value = DEFAULT_GAIN;
    this.inputPassthrough.connect(this.attenuator);
  }

  /** The output node — what gets registered as the mod source on the router. */
  get modOutput(): AudioNode {
    return this.attenuator;
  }

  /**
   * Register with the ModRouter. Idempotent; calling this twice is
   * harmless (registerSource just overwrites). The router will pick
   * up any existing connections that target 'wave' and wire them
   * once the source becomes available.
   */
  register(): void {
    if (this.registered) return;
    this.router.registerSource(
      WAVE_MOD_SOURCE_ID,
      this.attenuator,
      'WAVE',
      'Modulators',
    );
    this.registered = true;
  }

  /**
   * Set or change the tap point. Pass null to mute the source (the
   * registration stays on the router so existing mod connections
   * keep their wiring; they just won't receive any signal).
   */
  setSource(node: AudioNode | null): void {
    if (this.currentSource === node) return;
    if (this.currentSource) {
      try {
        this.currentSource.disconnect(this.inputPassthrough);
      } catch {
        /* already disconnected */
      }
    }
    this.currentSource = node;
    if (node) {
      try {
        node.connect(this.inputPassthrough);
      } catch {
        // Connection failed (incompatible nodes / closed context).
        // Leave currentSource set so a retry doesn't double-disconnect;
        // a future setSource call will replace it.
      }
    }
  }

  /**
   * Update the attenuator gain — the static "how loud is the wave
   * signal coming into the matrix" knob. The per-connection depth
   * is independent and managed by the router. Useful if a tap point
   * runs hot (e.g. master at full volume) and the user wants to
   * tame WAVE's contribution globally.
   */
  setGain(gain: number): void {
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    this.attenuator.gain.cancelScheduledValues(t);
    this.attenuator.gain.setTargetAtTime(Math.max(0, gain), t, 0.01);
  }

  /** Tear down — disconnect tap and unregister from the router. */
  dispose(): void {
    if (this.currentSource) {
      try {
        this.currentSource.disconnect(this.inputPassthrough);
      } catch {
        /* already disconnected */
      }
      this.currentSource = null;
    }
    if (this.registered) {
      this.router.unregisterSource(WAVE_MOD_SOURCE_ID);
      this.registered = false;
    }
    try {
      this.inputPassthrough.disconnect();
    } catch {
      /* already disconnected */
    }
    try {
      this.attenuator.disconnect();
    } catch {
      /* already disconnected */
    }
  }
}
